import { log } from "@app/app/services/log";

import { HostApiMethodData } from "../../api/methods";
import { ApiAdapter } from "../types";

/**
 * Pending API adapter that wraps another adapter and implements a pending mechanism
 * This allows for method calls to be queued until a handler is registered
 *
 * Sidenote: This adapter forces every method to be registered.
 */
export class PendingApiAdapter implements ApiAdapter {
    private wrappedAdapter: ApiAdapter;
    private pendingMethodCalls: Array<{
        methodName: string;
        args: unknown[];
        resolve: (value: unknown) => void;
        reject: (reason?: unknown) => void;
    }> = [];
    private registeredMethods = new Set<string>();

    private methodHandlers: Record<string, (...args: unknown[]) => unknown> = {} as Record<string, (...args: unknown[]) => unknown>;

    public constructor(adapter: ApiAdapter) {
        this.wrappedAdapter = adapter;
    }

    public setValue(key: string, value: any): void {
        this.wrappedAdapter.setValue(key, value);
    }

    public exposeApi(api: HostApiMethodData[]): void {
        this.wrappedAdapter.exposeApi(api);

        api.forEach((method) => {
            this.registerMethodHandler(
                method.methodName,
                (...args: unknown[]) =>
                    new Promise((resolve, reject) => {
                        log.debug("[PendingApiAdapter] Queueing call for method", { methodName: method.methodName });
                        this.pendingMethodCalls.push({
                            methodName: method.methodName,
                            args,
                            resolve,
                            reject,
                        });
                    })
            );
        });
    }

    public sendEventToHost(event: string, data?: unknown): void {
        this.wrappedAdapter.sendEventToHost(event, data);
    }

    public registerMethodHandler(method: string, callback: (...args: unknown[]) => Promise<unknown>): void {
        this.methodHandlers[method] = callback;

        this.registeredMethods.add(method);

        this.executePendingCalls(method);

        this.wrappedAdapter.registerMethodHandler(method, callback);
    }

    /**
     * Executes all pending calls for a specific method
     * @param methodName The method name to execute pending calls for
     */
    private executePendingCalls(methodName: string): void {
        const pendingCalls = this.pendingMethodCalls.filter((call) => call.methodName === methodName);
        const handler = this.methodHandlers[methodName];

        if (!handler || pendingCalls.length === 0) {
            return;
        }

        log.debug("[PendingApiAdapter] Executing pending calls", { methodName, pendingCallCount: pendingCalls.length });

        for (const call of pendingCalls) {
            try {
                // Execute the handler with the stored arguments
                Promise.resolve(handler(...call.args))
                    .then((result) => call.resolve(result))
                    .catch((error) => call.reject(error));
            } catch (error) {
                call.reject(error);
            }
        }

        // Remove the executed calls from the queue
        this.pendingMethodCalls = this.pendingMethodCalls.filter((call) => call.methodName !== methodName);
        log.debug("[PendingApiAdapter] Completed pending calls", { methodName, remainingCalls: this.pendingMethodCalls.length });
    }
}
